import { CircularProgress, TextField } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { loginSuccessAction, loginWithGoogleAction } from "redux/actions/authActions";
import { auth } from "firebaseConfig";
import { GoogleAuthProvider } from "firebase/auth";
import { signInWithPopup } from "firebase/auth";
import { AuthService } from "services/authService";
import { ADMIN_HOME_PATH } from "helpers/url";
import { FcGoogle } from "react-icons/fc";
import { FaLock, FaWhatsapp } from "react-icons/fa";
import { useEffect, useRef, useState } from "react";
import { getAuthUser } from "redux/selectors/getUser";
import { PhoneInput } from "components/PhoneInput/PhoneInput";
import { validatePhone } from "components/PhoneInput/countries";
import { OnboardLayout } from "components/OnboardLayout/OnboardLayout";
import {
  ALT_BUTTON_CLASS,
  AI_AGENT_FEATURE,
  AuthFeatures,
  AuthIllustration,
  CHATBOT_FEATURE,
  ErrorBox,
  FieldLabel,
  PRIMARY_BUTTON_CLASS,
} from "../components/AuthDecor";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(getAuthUser);

  const [phone, setPhone] = useState("");
  const [phonePrefix, setPhonePrefix] = useState(7);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Ошибки полей показываем после ухода с поля или после попытки войти
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const fieldErrors = {
    phone: validatePhone(phone, phonePrefix),
    password: password ? null : "Введите пароль",
  };
  const fieldError = (field) => ((submitted || touched[field]) && fieldErrors[field]) || null;
  const touch = (field) => () => setTouched((prev) => ({ ...prev, [field]: true }));
  // Вход выполнен на этой странице — после него показываем выбор организации
  const justLoggedIn = useRef(false);

  useEffect(() => {
    if (user) {
      // Организация ещё не выбрана — тоже показываем выбор
      const needSelectOrg = justLoggedIn.current || !localStorage.getItem("orgID");
      navigate(needSelectOrg ? "/auth/select-org" : ADMIN_HOME_PATH);
    }
  }, [user]);

  const signWithPhone = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitted(true);
    if (fieldErrors.phone || fieldErrors.password) {
      return;
    }
    setLoading(true);
    try {
      const { data } = await AuthService.loginWithPhone(phone.trim(), password);

      justLoggedIn.current = true;
      dispatch(
        loginSuccessAction({
          user: data.user,
          token: data.token,
        })
      );
    } catch (ex) {
      console.error("signWithPhone ex: ", ex);
      setError(
        ex?.response?.data?.message ||
          "Не удалось войти. Проверьте телефон и пароль"
      );
    } finally {
      setLoading(false);
    }
  };

  const signWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const firebaseAuthResponse = await signInWithPopup(auth, provider);
      console.log("firebaseAuthResponse:", firebaseAuthResponse);

      const firebaseUser = firebaseAuthResponse.user;

      const user = {
        email: firebaseUser.email,
      };

      const { data: responseUser } = await AuthService.signInWithGoogle(
        user,
        firebaseUser.accessToken
      );
      console.log("signWithGoogle: ", responseUser);


      justLoggedIn.current = true;
      dispatch(
        loginWithGoogleAction({
          user: responseUser.user,
          token: responseUser.token,
        })
      );
    } catch (ex) {
      console.error("signWithGoogle ex: ", ex);
    }
  };

  return (
    <>
      <Helmet>
        <title>Вход | Ubee AI</title>
        <meta name="description" content="Вход в кабинет Ubee AI" />
      </Helmet>
      <OnboardLayout
        title="С возвращением!"
        subtitle="Ubee AI отвечает клиентам, пока вы заняты делом"
        illustration={<AuthIllustration />}
        features={<AuthFeatures items={[CHATBOT_FEATURE, AI_AGENT_FEATURE]} />}
      >
        <h2 className="text-2xl sm:text-[28px] font-bold leading-snug mb-2">Вход в Ubee AI</h2>
        <p className="text-gray-500 mb-8">Войдите по номеру телефона и паролю</p>

        <form onSubmit={signWithPhone} noValidate className="flex flex-col gap-4">
          <div>
            <FieldLabel>Номер телефона</FieldLabel>
            <PhoneInput
              phoneNumber={phone}
              countryCode={phonePrefix}
              hideLabel={true}
              autoComplete="tel-national"
              error={fieldError("phone")}
              onBlur={touch("phone")}
              onChange={(params) => {
                if (params.countryCode) setPhonePrefix(params.countryCode);
                if (params.phone !== undefined) setPhone(params.phone);
              }}
            />
          </div>
          <div>
            <FieldLabel>Пароль</FieldLabel>
            <TextField
              fullWidth
              placeholder="Введите пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={touch("password")}
              error={Boolean(fieldError("password"))}
              helperText={fieldError("password")}
              type="password"
              autoComplete="current-password"
            />
          </div>

          {error && <ErrorBox>{error}</ErrorBox>}

          <button
            type="submit"
            disabled={loading}
            className={PRIMARY_BUTTON_CLASS}
          >
            {loading && <CircularProgress size={22} sx={{ color: "white" }} />}
            {loading ? "Входим…" : "Войти"}
          </button>
        </form>

        <div className="flex items-center gap-4 my-6 text-sm text-gray-400">
          <div className="h-px flex-1 bg-gray-200" />
          или
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        <div className="flex flex-col gap-3">
          <Link to="/auth/whatsapp/signin" className={ALT_BUTTON_CLASS}>
            <span className="w-7 h-7 rounded-lg bg-[#25d366] text-white flex items-center justify-center">
              <FaWhatsapp size={18} />
            </span>
            Войти через WhatsApp
          </Link>
          <button type="button" onClick={signWithGoogle} className={ALT_BUTTON_CLASS}>
            <span className="w-7 h-7 flex items-center justify-center">
              <FcGoogle size={22} />
            </span>
            Войти через Google
          </button>
        </div>

        <div className="mt-8 rounded-xl bg-purple-50 border border-purple-100 px-4 py-3 text-sm text-gray-700">
          Нет аккаунта?{" "}
          <Link to="/auth/register" className="font-semibold text-[#4a2366] hover:underline">
            Попробуйте бесплатно 7 дней
          </Link>{" "}
          — с чат-ботом, ИИ-агентом и демо-данными.
        </div>

        <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-400">
          <FaLock size={11} />
          <span>Данные передаются по защищённому соединению</span>
        </div>
      </OnboardLayout>
    </>
  );
};

export default Login;
